import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Connect, PropsFromRedux } from './SearchSectionContainer';
import { KeywordsCarouselWizard } from '../keywords-carousel/KeywordsCarouselWizard';
import { SearchAdviceWizard } from '../search-advice/SearchAdviceWizard';
import { SearchAutoCorrectWizard } from '../auto-correct/SearchAutoCorrectWizard';
import { ZeroResultsRouter } from '../zero-results/ZeroResultsRouter';
import { SortDropdown } from '../search-sort/sort-dropdown/SortDropdown';
import { MainSearchSnippets } from '../search-snippets/main/MainSearchSnippets';
import { AsideSearchSnippets } from '../search-snippets/aside/AsideSearchSnippets';
import { SearchDecisionWatcher } from '../search-decision-watcher/SearchDecisionWatcher';
import { SearchResults } from '../search-results/SearchResults';
import { TopLimitation } from '../top-limitation/TopLimitation';
import { MobileActions } from '../mobile-actions/MobileActions';
import { doctrineAnalyticsPage } from '../../../utils/analyticsUtils';
import styles from './search_section.module.scss';
import { withUser } from '../../../user/UserContext';
import { UserContextType } from '../../../user/user.types';
import { FilterBox } from '../search-snippets/aside/filter-box/FilterBox';
import { LoaderWithText } from '../../../components/loader/LoaderWithText';

type Props = PropsFromRedux & UserContextType;

const SEARCH_TYPE_ENTITY_MAP: { [s: string]: string } = {
  arret: 'app.pages.search.types.decision',
  commentary: 'app.pages.search.types.commentary',
  law: 'app.pages.search.types.legislation',
  lawyer: 'app.pages.search.types.lawyer',
  enterprise: 'app.pages.search.types.enterprise',
};

export const SEARCH_TYPES_WITH_FILTERS = ['arret', 'commentary', 'law'];

export class RawSearchSection extends React.Component<Props> {
  componentDidMount() {
    doctrineAnalyticsPage('SearchResults');
  }

  render() {
    const {
      loaded,
      searchLoading,
      error,
      paginationLoading,
      totalHits,
      hasResults,
      endOfResults,
      type,
      searchTime,
      loggedIn,
    } = this.props;
    const parsedTotalHits = Math.floor(totalHits);
    const isMaxExceeded = totalHits > parsedTotalHits;
    const seeAllResultsMessage =
      hasResults && loaded && endOfResults && loggedIn;
    return (
      <>
        <div className={styles.container}>
          <MobileActions />

          <div className={styles.desktopContainer}>
            <div className={styles.headerActions}>
              {!searchLoading && loaded && totalHits > -1 && (
                <span className={styles.headerStatus}>
                  <FormattedMessage
                    id={
                      isMaxExceeded
                        ? 'app.pages.search.results.totalLabelExeeded'
                        : 'app.pages.search.results.totalLabel'
                    }
                    values={{
                      totalHits: parsedTotalHits,
                      searchTime: formatTime(searchTime),
                      entity: (
                        <FormattedMessage
                          id={SEARCH_TYPE_ENTITY_MAP[type]}
                          values={{
                            totalHits: parsedTotalHits,
                            searchTime: formatTime(searchTime),
                          }}
                        />
                      ),
                    }}
                  />
                </span>
              )}
              {searchLoading && (
                <span className={styles.headerStatus}>
                  <LoaderWithText>
                    <FormattedMessage id="app.pages.search.loading" />
                  </LoaderWithText>
                </span>
              )}

              <div className="hide_on--tablet">
                {(type === 'arret' || type === 'commentary') && (
                  <SortDropdown />
                )}

                {loaded && totalHits > 0 && type === 'arret' && (
                  <div className={styles.addWatcher}>
                    <SearchDecisionWatcher />
                  </div>
                )}
              </div>
            </div>

            <SearchAdviceWizard />
            <SearchAutoCorrectWizard />
            <div className={styles.subContainer}>
              <MainSearchSnippets />
              <div className={styles.asideContainer}>
                {SEARCH_TYPES_WITH_FILTERS.includes(type) && (
                  <FilterBox
                    searchType={type as 'arret' | 'commentary' | 'law'}
                  />
                )}
                <AsideSearchSnippets />
              </div>

              {loaded && (
                <div className={styles.results}>
                  <KeywordsCarouselWizard />
                  <SearchResults />
                  <TopLimitation />
                </div>
              )}

              {paginationLoading && (
                <div className={styles.loading}>
                  <span>
                    <LoaderWithText>
                      <FormattedMessage id="app.pages.search.loading" />
                    </LoaderWithText>
                  </span>
                </div>
              )}

              {seeAllResultsMessage && (
                <div className={styles.endOfResults}>
                  <FormattedMessage id="app.pages.search.endOfResults" />
                </div>
              )}

              {error && (
                <div className={styles.errorContainer}>
                  <div
                    className={styles.error}
                    dangerouslySetInnerHTML={{ __html: error }}
                  />
                </div>
              )}

              <ZeroResultsRouter />
            </div>
          </div>
        </div>
      </>
    );
  }
}

export const SearchSection = Connect(withUser(RawSearchSection));

function formatTime(time: number) {
  return (time / 1000).toFixed(2).replace('.', ',');
}
